import React from "react";
import { useTranslation } from "react-i18next";

import { useNotification } from "../../shared/hooks";
import { PointsConfig } from "../points-config";
import {
  deleteComboSchema,
  getComboAddonSchemas,
  getComboSchemas,
  getPointsConfig,
  updateComboSchema,
} from "../combos/api";
import { comboForSave } from "../combos/pricing";
import { ComboAddonSchemaWithUsage, ComboSchema } from "../combos/types";

export type SavedCombo = ComboSchema & { id: string };

/**
 * Combos, add-ons and the points config for the menu editor, with the inline
 * operations of a section's combo list (sold out, reorder, image, delete).
 */
export function useMenuCombos(restaurantId: string) {
  const { t } = useTranslation();
  const { deleteSuccess, notifyError } = useNotification();
  const [combos, setCombos] = React.useState<SavedCombo[] | null>(null);
  const [addons, setAddons] = React.useState<ComboAddonSchemaWithUsage[]>([]);
  const [pointsConfig, setPointsConfig] = React.useState<PointsConfig | undefined>(undefined);
  const [busyIds, setBusyIds] = React.useState<Set<string>>(new Set());

  const refreshCombos = React.useCallback(() => {
    getComboSchemas(
      restaurantId,
      undefined,
      (result) => setCombos(result.filter((c): c is SavedCombo => !!c.id)),
      (err) => {
        notifyError(t("combo_failed_to_load"), err);
        setCombos([]);
      }
    );
  }, [restaurantId]);

  const refreshAddons = React.useCallback(() => {
    getComboAddonSchemas(restaurantId, undefined, setAddons, (err) =>
      notifyError(t("combo_failed_to_load_addons"), err)
    );
  }, [restaurantId]);

  // Combo membership of items changes when an item is deleted (cascade), so
  // callers refresh both.
  const refresh = React.useCallback(() => {
    refreshCombos();
    refreshAddons();
  }, [refreshCombos, refreshAddons]);

  React.useEffect(() => {
    refresh();
    getPointsConfig(restaurantId, setPointsConfig);
  }, [restaurantId]);

  const setBusy = (id: string, busy: boolean) =>
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (busy) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });

  const replaceLocal = (combo: SavedCombo) =>
    setCombos((prev) => {
      if (!prev) {
        return prev;
      }
      return prev.some((c) => c.id === combo.id)
        ? prev.map((c) => (c.id === combo.id ? combo : c))
        : [...prev, combo];
    });

  // PUTs a full combo (the API replaces it) and updates local state on success.
  const saveCombo = (combo: SavedCombo): Promise<void> => {
    setBusy(combo.id, true);
    return new Promise((resolve, reject) => {
      updateComboSchema(
        restaurantId,
        combo.id,
        comboForSave(combo),
        () => {
          setBusy(combo.id, false);
          replaceLocal(combo);
          resolve();
        },
        (err) => {
          setBusy(combo.id, false);
          reject(err);
        }
      );
    });
  };

  const setSoldOut = (combo: SavedCombo, soldOut: boolean) =>
    saveCombo({ ...combo, sold_out: soldOut }).catch((err) => notifyError(t("error"), err));

  // Moves a combo within its section and rewrites seq of the section's
  // combos to their new positions, saving only those that changed.
  const moveCombo = (sectionCombos: ReadonlyArray<SavedCombo>, from: number, to: number) => {
    const next = [...sectionCombos];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    next.forEach((combo, seq) => {
      if (combo.seq !== seq) {
        saveCombo({ ...combo, seq }).catch((err) => notifyError(t("error"), err));
      }
    });
  };

  const removeCombo = (combo: SavedCombo) => {
    setBusy(combo.id, true);
    deleteComboSchema(
      restaurantId,
      combo.id,
      () => {
        setBusy(combo.id, false);
        setCombos((prev) => (prev ? prev.filter((c) => c.id !== combo.id) : prev));
        deleteSuccess(combo.name);
        // The add-ons' used_by changed.
        refreshAddons();
      },
      (err) => {
        setBusy(combo.id, false);
        notifyError(t("error"), err);
      }
    );
  };

  const addonCreated = (addon: ComboAddonSchemaWithUsage) =>
    setAddons((prev) => [...prev.filter((a) => a.id !== addon.id), addon]);

  return {
    combos,
    addons,
    pointsConfig,
    busyIds,
    refresh,
    refreshAddons,
    replaceLocal,
    saveCombo,
    setSoldOut,
    moveCombo,
    removeCombo,
    addonCreated,
  };
}
